import React, { useState, useEffect, useRef } from "react";
import { Box, Card, Typography, ButtonGroup, Button } from '@mui/material';
import { LabelValueViewModel } from "../../Types/ExportTypes";
import { useAuth } from '../../Context/Contexts';
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const ShopAvgValueGraph: React.FC = () => {
  const { authService } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState(3);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LabelValueViewModel[]>([]);
  const initiatedRef = useRef(false);

  const buildData = (items?: LabelValueViewModel[]) => {
    const sorted = [...(items ?? data)].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    return {
      labels: sorted.map(i => i.label),
      datasets: [
        {
          label: t("common.amount"),
          data: sorted.map(i => i.value),
          backgroundColor: "#1976d2"
        }
      ]
    };
  };

  const [graph, setGraph] = useState(buildData);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: t("graph.shopAvgValueGraphTitle") }
    }
  };

  const loadData = async (override?: number | null) => {
    const m = override ?? mode;
    setLoading(true);
    try {
      const resp = await authService.get<{ items: LabelValueViewModel[] }>(`/graphApi/GetShopAvgValueGraph?mode=${m}`);
      setData(() => { const g = buildData(resp.items); setGraph(g); return resp.items; });
    } catch (err) {
      console.error("GetShopAvgValueGraph failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!initiatedRef.current) { initiatedRef.current = true; loadData(); } }, []);

  return (
    <Card>
      <Box sx={{ backgroundColor: "#fafafa", borderRadius: 2, p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: "flex", justifyContent: "center" }}>
          {t("graph.shopAvgValueGraphTitle")}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <ButtonGroup variant="outlined">
            <Button onClick={() => { setMode(1); loadData(1); }} variant={mode === 1 ? "contained" : "outlined"}>{t("common.day")}</Button>
            <Button onClick={() => { setMode(2); loadData(2); }} variant={mode === 2 ? "contained" : "outlined"}>{t("common.week")}</Button>
            <Button onClick={() => { setMode(3); loadData(3); }} variant={mode === 3 ? "contained" : "outlined"}>{t("common.month")}</Button>
            <Button onClick={() => { setMode(4); loadData(4); }} variant={mode === 4 ? "contained" : "outlined"}>{t("common.year")}</Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ height: 500, display: 'flex', justifyContent: 'center' }}>
          {loading ? <Typography>Loading...</Typography> : <Bar data={graph} options={options} />}
        </Box>
      </Box>
    </Card>
  );
};

export default ShopAvgValueGraph;
